// ─────────────────────────────────────────────────────────────────────────────
// Roles y Permisos de Pulso (grupo Pulso del panel, 2026-08-08, petición de Adriana).
//
// Copia de la pantalla Roles y Permisos de Brillamos (`views/recognition/admin/AdminRoles.tsx`):
// mismo contenedor (AdminSection), toolbar con búsqueda + botón acento, tabla "glass", estados de
// carga/error/vacío (StateBlock) y pie de paginación (AdminPagerFooter). Reutiliza esos componentes
// y `recognition.css` para que se vea IGUAL que Brillamos. Administra a los admins de PULSO
// (AdminPulso / AdminOU); el alta de admins GLOBALES vive aparte, en la entrada lateral
// "Administradores". La edición de rol / alcance / estado / permisos va en un MODAL.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canManageUser } from '@/auth/roleGrants';
import { ROLE_LABEL_KEY } from '@/auth/roleLabels';
import type { AdminUser } from '@/data/mockUsers';
import type { UserRole } from '@/hooks/useCurrentUser';
import { useAdminUsers } from '@/hooks/useUsers';
import { useOUItems } from '@/hooks/useOUs';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { InitialsAvatar } from '@/components/common';
import { AdminSection, AdminPagerFooter, usePagination } from '@/views/recognition/admin/adminShared';
import { StateBlock } from '@/views/recognition/components';
import { RolesPulsoEditorModal, type PulsoEditorTarget } from './RolesPulsoEditorModal';
import '@/views/recognition/recognition.css';

/** Roles de Pulso que administra esta pantalla. */
const PULSO_ADMIN_ROLES: UserRole[] = ['AdminPulso', 'AdminOU'];

export function RolesPulsoTab() {
  const { t } = useTranslation();
  const { activeRoles } = useVisibleNavLinks();
  const admins = useAdminUsers();
  const { data: ouItems = [] } = useOUItems();

  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<PulsoEditorTarget | null>(null);

  const users = useMemo(() => admins.data ?? [], [admins.data]);
  const ouNameById = useMemo(() => new Map(ouItems.map(ou => [ou.id, ou.name])), [ouItems]);
  const pulsoAdmins = useMemo(
    () => users.filter(u => u.roles.some(role => PULSO_ADMIN_ROLES.includes(role))),
    [users],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pulsoAdmins;
    return pulsoAdmins.filter(u => (u.email ?? '').toLowerCase().includes(q) || u.name.toLowerCase().includes(q));
  }, [pulsoAdmins, query]);
  const pg = usePagination(filtered, 10);

  const scopeText = (u: AdminUser) =>
    (u.isAllOusScope || u.ouScopeIds.length === 0)
      ? t('admin.rolesPulsoAllScopes')
      : u.ouScopeIds.map(id => ouNameById.get(id) ?? id).join(', ');

  return (
    <AdminSection title={t('admin.rolesPulsoTitle')} description={t('admin.roles.desc', { ns: 'recognition' })}>
      <div className="admin-roles-panel">
        <div className="adm-roles-toolbar">
          <div className="adm-search">
            <i className="fas fa-magnifying-glass" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('admin.filterEmailPlaceholder')}
              aria-label={t('admin.filterEmailPlaceholder')}
            />
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setEditor({ mode: 'create' })}>
            <i className="fas fa-user-plus" /> {t('admin.addAdmin')}
          </button>
        </div>

        <StateBlock
          isLoading={admins.isLoading}
          isError={admins.isError}
          error={admins.error}
          isEmpty={filtered.length === 0}
          emptyText={t('admin.noUsersFound')}
          onRetry={() => { admins.refetch(); }}
        >
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--glass">
              <thead>
                <tr>
                  <th>{t('admin.colUser')}</th>
                  <th>{t('admin.roles')}</th>
                  <th>{t('admin.colAlcance')}</th>
                  <th>{t('admin.colEstado')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pg.slice.map((user: AdminUser, posicion: number) => {
                  const canManage = canManageUser(activeRoles, user.roles);
                  const pulsoRole = user.roles.find(role => PULSO_ADMIN_ROLES.includes(role));
                  // Índice GLOBAL de la lista completa, no de la página visible: mismo defecto que
                  // GestionAdministradores.tsx/AdminRoles.tsx (ronda de correcciones 1, Task 5).
                  const posicionGlobal = (pg.page - 1) * pg.pageSize + posicion;
                  return (
                    <tr key={user.id} className={editor?.mode === 'edit' && editor.admin.id === user.id ? 'adm-row--selected' : ''}>
                      <td>
                        <div className="adm-admin-cell">
                          <InitialsAvatar name={user.name || user.email || '?'} baseClass="rp-avatar" colorIndex={posicionGlobal} />
                          <div>
                            <div className="adm-admin-name">{user.name}</div>
                            <div className="adm-admin-email">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {pulsoRole && <span className="adm-role-badge">{t(ROLE_LABEL_KEY[pulsoRole])}</span>}
                      </td>
                      <td>{scopeText(user)}</td>
                      <td>
                        <span className={`adm-status ${user.active ? 'adm-status--on' : 'adm-status--off'}`}>
                          {t(user.active ? 'admin.roles.active' : 'admin.roles.inactive', { ns: 'recognition' })}
                        </span>
                      </td>
                      <td>
                        {canManage && (
                          <button type="button" className="btn btn--link" onClick={() => setEditor({ mode: 'edit', admin: user })}>
                            <i className="fas fa-sliders" /> {t('common.edit')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <AdminPagerFooter total={filtered.length} page={pg.page} pageSize={pg.pageSize} onPage={pg.setPage} onPageSize={pg.setPageSize} />
        </StateBlock>
      </div>

      <RolesPulsoEditorModal
        target={editor}
        existingAdmins={users}
        ouItems={ouItems}
        onClose={() => setEditor(null)}
        onSaved={() => { admins.refetch(); }}
      />
    </AdminSection>
  );
}

export default RolesPulsoTab;
